import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Icon, type IconName } from '@/components/icon';
import { Logo } from '@/components/logo';
import { useAuth } from '@/auth/auth-context';

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconName;
  roles?: ReadonlyArray<string>;
  permission?: string;
  end?: boolean;
  testId?: string;
}

interface NavGroup {
  labelKey: string;
  items: ReadonlyArray<NavItem>;
}

const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    labelKey: 'group_content',
    items: [
      { to: '/resources', labelKey: 'resources', icon: 'image' },
      { to: '/composers', labelKey: 'composers', icon: 'layers' },
    ],
  },
  {
    labelKey: 'group_fleet',
    items: [
      { to: '/players', labelKey: 'players', icon: 'film' },
      { to: '/planning', labelKey: 'planning', icon: 'calendar' },
      { to: '/groups', labelKey: 'groups', icon: 'layers' },
      { to: '/demand', labelKey: 'demand', icon: 'alert-triangle', permission: 'composers.update', testId: 'sidebar-demand' },
    ],
  },
  {
    labelKey: 'group_insights',
    items: [
      { to: '/', labelKey: 'dashboard', icon: 'check-circle', end: true },
      { to: '/audit', labelKey: 'audit', icon: 'check', roles: ['admin', 'auditor'] },
    ],
  },
  {
    labelKey: 'group_admin',
    items: [
      { to: '/users', labelKey: 'users', icon: 'user', roles: ['admin'] },
      { to: '/settings', labelKey: 'settings', icon: 'settings', roles: ['admin'] },
      { to: '/queue', labelKey: 'queue', icon: 'layers', roles: ['admin'] },
      { to: '/queues', labelKey: 'queues', icon: 'layers', permission: 'queue.admin', testId: 'sidebar-queues' },
      { to: '/counters', labelKey: 'counters', icon: 'layers', permission: 'queue.admin', testId: 'sidebar-counters' },
      { to: '/attendant', labelKey: 'attendant', icon: 'user', permission: 'queue.attend', testId: 'sidebar-attendant' },
      { to: '/menu', labelKey: 'menu', icon: 'layers', permission: 'queue.admin', testId: 'sidebar-menu' },
      { to: '/kitchen', labelKey: 'kitchen', icon: 'layers', permission: 'queue.admin', testId: 'sidebar-kitchen' },
      { to: '/software', labelKey: 'software', icon: 'layers', roles: ['admin'] },
    ],
  },
  {
    // Platform-level (super_admin only): the sole item is gated on the platform
    // permission, and empty groups are filtered out below — so the whole group
    // is invisible to every non-platform user.
    labelKey: 'group_platform',
    items: [
      {
        to: '/platform/tenants',
        labelKey: 'platform_tenants',
        icon: 'layers',
        permission: 'platform.tenants.manage',
        testId: 'sidebar-platform-tenants',
      },
    ],
  },
];

const COLLAPSE_KEY = 'displaycms.admin.sidebar.collapsed';

// The nav body (permission-filtered groups + links), shared by the desktop
// sidebar and the mobile drawer. `onNavigate` lets the drawer close on a link
// click; `collapsed` hides the labels (desktop icon-rail only).
export function SidebarNav({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const { t } = useTranslation(['nav']);
  const { currentUser, hasPermission } = useAuth();
  const userRoles = currentUser?.roles ?? [];

  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => {
      const rolesOk =
        !it.roles || it.roles.some((r) => (userRoles as string[]).includes(r));
      const permissionOk = !it.permission || hasPermission(it.permission);
      return rolesOk && permissionOk;
    }),
  })).filter((g) => g.items.length > 0);

  return (
    <>
      {visibleGroups.map((group) => (
        <div key={group.labelKey} className="mb-4">
          {!collapsed && (
            <div className="mb-1 px-2 text-xs font-semibold uppercase tracking-wider text-white/50">
              {t(group.labelKey)}
            </div>
          )}
          <ul>
            {group.items.map((it) => (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  onClick={onNavigate}
                  {...(it.end === true ? { end: true } : {})}
                  {...(it.testId ? { 'data-testid': it.testId } : {})}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-accent-foreground))]'
                        : 'hover:bg-white/5'
                    }`
                  }
                >
                  <Icon name={it.icon} size={18} aria-label={t(it.labelKey)} />
                  {!collapsed && <span>{t(it.labelKey)}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

export function Sidebar(): JSX.Element {
  const { t } = useTranslation(['nav']);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(COLLAPSE_KEY) === 'true';
  });

  // Only persist after the first user-driven change; skip the initial render
  // so that localStorage stays null when the sidebar has never been toggled.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    localStorage.setItem(COLLAPSE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'b' && e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setCollapsed((c) => !c);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <nav
      data-testid="sidebar"
      data-collapsed={collapsed}
      className={`hidden h-screen flex-col bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] transition-all duration-200 md:flex ${
        collapsed ? 'w-16' : 'w-64'
      }`}
      aria-label={t('primary_navigation')}
    >
      <NavLink
        to="/"
        data-testid="sidebar-brand"
        className={`flex h-14 shrink-0 items-center border-b border-white/10 ${
          collapsed ? 'justify-center px-0' : 'px-3'
        }`}
        aria-label="displaycms"
      >
        <Logo collapsed={collapsed} />
      </NavLink>
      <div className="flex-1 overflow-y-auto p-3">
        <SidebarNav collapsed={collapsed} />
      </div>
      <div className="border-t border-white/10 p-2">
        <button
          type="button"
          data-testid="sidebar-collapse-toggle"
          onClick={() => setCollapsed((c) => !c)}
          className="flex w-full items-center justify-center gap-2 rounded-md p-2 text-sm hover:bg-white/5"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={collapsed ? 'panel-left-open' : 'panel-left-close'} size={18} />
        </button>
      </div>
    </nav>
  );
}
